/**
 * Cart BOGO Quantity - Auto-adds free gift(s) when customer buys X+ of a trigger product.
 * Scales: buy 2 → 1 gift, buy 4 → 2 gifts, buy 6 → 3 gifts, etc.
 *
 * Uses property `_bogo_quantity_gift: "true"` to tag auto-added gifts.
 * Works independently from other BOGO systems (collection, routines).
 *
 * Two fixes ported from grid-2027's own already-corrected version of this
 * same promo (see assets/cart-bogo-quantity.js and
 * snippets/cart-bogo-quantity.liquid there):
 * - Trigger matching is now by VARIANT id (data-trigger-variant-ids, every
 *   variant of the configured trigger product), not product id — this
 *   store uses combined listings, where a cart line for the trigger
 *   product can report a different (source) product id than the
 *   merchant-configured (listing) one, silently breaking product-id
 *   matching for that line. Confirmed real report: decreasing/removing
 *   the trigger product's quantity left the free gift stuck at its old,
 *   now-over-entitled quantity, because the reconciler's product-id check
 *   never recognized the change on that line.
 * - The gift lock is now the shared, owner-aware, TTL-bounded
 *   window.__lvGiftLock used by every other gift watcher in this theme,
 *   not an isolated window.__bogoQuantityGiftLock boolean — this promo's
 *   add/update/remove could previously race against cart-bogo-routines.js,
 *   cart-bogo-litres.js, etc. with no mutual exclusion at all.
 */

const BOGO_DEBUG = false;

/**
 * Shared, owner-aware, TTL-bounded lock at window.__lvGiftLock so this
 * watcher can never race an add/change against the other gift watchers
 * (cart-bogo-routines.js, cart-bogo-litres.js, etc.) — same pattern and
 * rationale as those files. This previously used its own, unshared
 * window.__bogoQuantityGiftLock boolean, so it was never mutually excluded
 * with the others at all.
 */
const BOGO_QUANTITY_LOCK_OWNER = 'bogo-quantity';
const BOGO_QUANTITY_LOCK_TTL_MS = 10000;

function acquireQuantityGiftLock() {
  const held = window.__lvGiftLock;
  if (held && Date.now() - held.at < BOGO_QUANTITY_LOCK_TTL_MS) return false;
  window.__lvGiftLock = { owner: BOGO_QUANTITY_LOCK_OWNER, at: Date.now() };
  return true;
}

function releaseQuantityGiftLock() {
  const held = window.__lvGiftLock;
  if (held && held.owner === BOGO_QUANTITY_LOCK_OWNER) window.__lvGiftLock = null;
}

if (!customElements.get('cart-bogo-quantity')) {
  customElements.define('cart-bogo-quantity', class extends HTMLElement {
    #activeRequest = null;
    #isUpdating = false;
    #triggerProductId = null;
    /** @type {Set<string>} */
    #triggerVariantIds = new Set();
    #giftProductHandle = null;
    #giftVariantId = null;
    #minQuantity = 2;

    connectedCallback() {
      this.#triggerProductId = this.dataset.triggerProductId || null;
      this.#giftProductHandle = this.dataset.giftProductHandle || null;
      this.#giftVariantId = this.dataset.giftVariantId || null;
      this.#minQuantity = parseInt(this.dataset.minQuantity) || 2;

      /*
       * This store uses combined listings: when the merchant picks a
       * listing as the trigger product, a cart line for it reports the
       * SOURCE product, whose product id differs from the listing's — so
       * matching by product id alone silently fails to recognize that line
       * at all. Confirmed real report: decreasing/removing the trigger
       * line left the gift stuck at its old, now-over-entitled quantity,
       * because #checkBogoCondition's product-id match never saw the
       * decrease. Variant ids are identical on both sides, so every
       * variant id of the configured trigger product is matched instead —
       * see snippets/cart-bogo-quantity.liquid's own comment on emitting
       * data-trigger-variant-ids for the same reasoning.
       */
      try {
        const ids = JSON.parse(this.dataset.triggerVariantIds || '[]');
        this.#triggerVariantIds = new Set(ids.map((id) => String(id)));
      } catch (e) {
        this.#triggerVariantIds = new Set();
      }

      BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Connected:', {
        trigger: this.#triggerProductId,
        triggerVariantIds: Array.from(this.#triggerVariantIds),
        gift: this.#giftProductHandle,
        variantId: this.#giftVariantId,
        minQty: this.#minQuantity,
      });

      if (!this.#triggerProductId || this.#triggerVariantIds.size === 0) {
        console.error('🛒 [BOGO Quantity] Missing trigger product configuration');
        return;
      }
      if (!this.#giftProductHandle && !this.#giftVariantId) {
        console.error('🛒 [BOGO Quantity] Missing gift product configuration');
        return;
      }

      // If no variant ID provided, fetch it
      if (!this.#giftVariantId && this.#giftProductHandle) {
        this.#fetchGiftVariantId();
        return;
      }

      this.#init();
    }

    #init() {
      document.addEventListener('cart:update', this.#handleCartUpdate);

      // Check on initial load — stagger to avoid race with other BOGO systems
      setTimeout(() => this.#checkBogoCondition(), 300);
      setTimeout(() => this.#checkBogoCondition(), 1500);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.#handleCartUpdate);
      this.#activeRequest?.abort();
    }

    async #fetchGiftVariantId() {
      try {
        const response = await fetch(`/products/${this.#giftProductHandle}.js`);
        if (response.ok) {
          const product = await response.json();
          if (product.variants?.length > 0) {
            this.#giftVariantId = product.variants[0].id.toString();
            BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Fetched gift variant ID:', this.#giftVariantId);
          }
        }
      } catch (error) {
        console.error('🛒 [BOGO Quantity] Failed to fetch gift product variant:', error);
      } finally {
        // Always init so the component listens for cart events even if the
        // initial variant fetch fails — checks will simply no-op until the
        // variant id is recovered on a later cart update.
        this.#init();
      }
    }

    #handleCartUpdate = async (event) => {
      const source = event.detail?.data?.source || event.detail?.source;
      if (source === 'cart-bogo-quantity') return;
      await this.#checkBogoCondition();
    };

    /**
     * Refresh the cart drawer by dispatching the theme's native cart:update event.
     * CartItemsComponent will detect no sections data and call sectionRenderer.renderSection()
     * which fetches fresh HTML and morphs the DOM properly.
     */
    #refreshCartDrawer() {
      // Dispatch from inside the drawer (but NOT from cart-items-component itself,
      // because it ignores events where event.target === this)
      const drawer = document.querySelector('cart-drawer-component');
      const target = this.closest('cart-drawer-component') || drawer || document;

      target.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'cart-bogo-quantity',
          },
        },
      }));
      BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Dispatched cart:update → theme will re-render');
    }

    async #checkBogoCondition() {
      BOGO_DEBUG && console.log('🛒 [BOGO Quantity] checkBogoCondition called', { isUpdating: this.#isUpdating, triggerProductId: this.#triggerProductId });
      if (this.#isUpdating || this.#triggerVariantIds.size === 0) return;

      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;
        const cartData = await response.json();

        // Count trigger product quantity (exclude all gift types). Matches
        // by variant id (see connectedCallback's comment on combined
        // listings) -- not product_id, which a combined-listing cart line
        // can report differently from the merchant-configured trigger
        // product.
        let triggerQuantity = 0;
        for (const item of cartData.items || []) {
          if (item.properties?._bogo_quantity_gift === 'true') continue;
          if (item.properties?._bogo_collection_gift === 'true') continue;
          if (item.properties?._lv_bogo_same_product_gift === 'true') continue;
          if (item.properties?._auto_added_threshold === 'true') continue;
          if (item.properties?._freegifincart === 'true') continue;
          if (item.properties?._free_gift === 'true') continue;

          if (this.#triggerVariantIds.has(String(item.variant_id))) {
            triggerQuantity += item.quantity;
          }
        }

        // Calculate how many gifts they're entitled to
        const entitledGifts = Math.floor(triggerQuantity / this.#minQuantity);

        // Find current gift item and its quantity
        const giftItem = (cartData.items || []).find(
          item => item.properties?._bogo_quantity_gift === 'true'
        );
        const currentGiftQty = giftItem ? giftItem.quantity : 0;

        BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Check:', {
          triggerQty: triggerQuantity,
          entitled: entitledGifts,
          currentGiftQty,
          giftVariantId: this.#giftVariantId,
        });

        if (entitledGifts > 0 && currentGiftQty === 0 && this.#giftVariantId) {
          BOGO_DEBUG && console.log('🛒 [BOGO Quantity] → Adding', entitledGifts, 'gift(s)');
          await this.#addBogoGift(entitledGifts);
        } else if (entitledGifts === 0 && currentGiftQty > 0) {
          BOGO_DEBUG && console.log('🛒 [BOGO Quantity] → Removing gifts');
          await this.#removeBogoGift(giftItem);
        } else if (entitledGifts > 0 && currentGiftQty !== entitledGifts && giftItem) {
          BOGO_DEBUG && console.log('🛒 [BOGO Quantity] → Updating gift qty to', entitledGifts);
          await this.#updateGiftQuantity(giftItem, entitledGifts);
        }
      } catch (error) {
        console.error('🛒 [BOGO Quantity] Failed to check cart:', error);
      }
    }

    async #addBogoGift(quantity) {
      if (this.#isUpdating || !this.#giftVariantId) return;
      if (!acquireQuantityGiftLock()) {
        BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Another watcher holds the gift lock, skipping');
        return;
      }

      this.#isUpdating = true;

      try {
        // Double-check gift variant isn't already in cart
        const checkResponse = await fetch('/cart.js');
        if (!checkResponse.ok) {
          throw new Error(`Pre-check /cart.js failed: ${checkResponse.status}`);
        }
        const checkCart = await checkResponse.json();
        const existing = (checkCart.items || []).find(
          item => item.properties?._bogo_quantity_gift === 'true'
        );
        if (existing) {
          if (existing.quantity !== quantity) {
            // Release the lock + #isUpdating before delegating —
            // #updateGiftQuantity acquires both itself.
            this.#isUpdating = false;
            releaseQuantityGiftLock();
            await this.#updateGiftQuantity(existing, quantity);
          }
          return;
        }

        this.#activeRequest?.abort();
        this.#activeRequest = new AbortController();

        const sectionId = this.dataset.sectionId;
        const formData = new FormData();
        formData.append('id', this.#giftVariantId);
        formData.append('quantity', quantity.toString());
        formData.append('properties[_bogo_quantity_gift]', 'true');
        formData.append('properties[_free_gift]', 'true');
        if (sectionId) formData.append('sections', sectionId);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          signal: this.#activeRequest.signal,
        });

        const data = await response.json();
        BOGO_DEBUG && console.log('🛒 [BOGO Quantity] /cart/add.js response:', { ok: response.ok, status: response.status, sectionId, hasSections: !!data.sections, sectionKeys: data.sections ? Object.keys(data.sections) : [] });

        if (!response.ok) throw new Error(`Failed to add BOGO quantity gift: ${response.status}`);

        BOGO_DEBUG && console.log(`🛒 [BOGO Quantity] ${quantity} gift(s) added successfully`);

        // Let the theme re-render the cart drawer
        this.#refreshCartDrawer();

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('🛒 [BOGO Quantity] Error adding gift:', error);
        }
      } finally {
        // Single release point — guarantees lock + flag never leak even when
        // upstream fetches throw or return non-OK. A harmless no-op if the
        // early-return path above already released both (the lock is
        // owner-checked, so releasing an already-released/reassigned lock
        // does nothing).
        this.#isUpdating = false;
        releaseQuantityGiftLock();
      }
    }

    async #updateGiftQuantity(giftItem, newQuantity) {
      if (this.#isUpdating) return;
      if (!acquireQuantityGiftLock()) {
        BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Another watcher holds the gift lock, skipping update');
        return;
      }
      this.#isUpdating = true;

      try {
        this.#activeRequest?.abort();
        this.#activeRequest = new AbortController();

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: newQuantity,
            sections: this.dataset.sectionId || undefined,
          }),
          signal: this.#activeRequest.signal,
        });

        if (response.ok) {
          BOGO_DEBUG && console.log(`🛒 [BOGO Quantity] Gift quantity updated to ${newQuantity}`);
          this.#refreshCartDrawer();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('🛒 [BOGO Quantity] Failed to update gift quantity:', error);
        }
      } finally {
        this.#isUpdating = false;
        releaseQuantityGiftLock();
      }
    }

    async #removeBogoGift(giftItem) {
      if (this.#isUpdating) return;
      if (!acquireQuantityGiftLock()) {
        BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Another watcher holds the gift lock, skipping removal');
        return;
      }
      this.#isUpdating = true;

      try {
        if (!giftItem) {
          const cartResponse = await fetch('/cart.js');
          if (!cartResponse.ok) return;
          const cart = await cartResponse.json();
          giftItem = (cart.items || []).find(
            item => item.properties?._bogo_quantity_gift === 'true'
          );
        }

        if (giftItem) {
          this.#activeRequest?.abort();
          this.#activeRequest = new AbortController();

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 0,
              sections: this.dataset.sectionId || undefined,
            }),
            signal: this.#activeRequest.signal,
          });

          if (response.ok) {
            BOGO_DEBUG && console.log('🛒 [BOGO Quantity] Gift(s) removed');
            this.#refreshCartDrawer();
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('🛒 [BOGO Quantity] Failed to remove gift:', error);
        }
      } finally {
        this.#isUpdating = false;
        releaseQuantityGiftLock();
      }
    }
  });
}
